/* The controls both desks share, in the form the V3 handoff drew them.
 *
 * Nothing here fetches. A screen hands in the options, the dirty flag, or the
 * sentence, and this file draws them. The console's older Seg2, Empty and
 * InfoTip stay on their own exports until a desk switches to these.
 */

import { createPortal } from "react-dom";
import {
  Children,
  cloneElement,
  isValidElement,
  useEffect,
  useId,
  useRef,
  useState,
  type DependencyList,
  type ReactElement,
  type ReactNode,
} from "react";

const SAVED = /^Saved/;

declare global {
  interface Window {
    __deskDirty?: (() => boolean) | null;
  }
}

/** How long a toast stays up. A save is brief; anything else is read. */
export function toastDuration(message: string): number {
  return SAVED.test(message) ? 2400 : 5200;
}

/** Hold one sentence, then clear it. `say` is what a save handler calls. */
export function useToast(): { message: string | null; say: (next: string) => void } {
  const [message, setMessage] = useState<string | null>(null);
  useEffect(() => {
    if (message === null) return;
    const timer = window.setTimeout(() => setMessage(null), toastDuration(message));
    return () => window.clearTimeout(timer);
  }, [message]);
  return { message, say: (next) => setMessage(next) };
}

/** The note inside the frame. A message that starts "Saved" gets the tick. */
export function Toast({ message }: { message: string | null }): JSX.Element | null {
  if (message === null || message === "") return null;
  const saved = SAVED.test(message);
  return (
    <div className={saved ? "adm-toast is-saved" : "adm-toast"} role="status">
      {saved ? (
        <svg className="toast-i" viewBox="0 0 16 16" aria-hidden="true">
          <path
            d="M3.5 8.5l3 3 6-7"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ) : null}
      <span>{message}</span>
    </div>
  );
}

/**
 * One control, two faces: the segmented row on a wide desk, a native dropdown
 * on a narrow one. More than four options drop to the menu below 1100 as well.
 */
export function Seg<Option extends string>({
  label,
  value,
  options,
  onPick,
}: {
  label: string;
  value: Option;
  options: readonly Option[];
  onPick: (option: Option) => void;
}): JSX.Element {
  const many = options.length > 4;
  return (
    <span className={many ? "segx seg-many" : "segx"}>
      <span className="seg2" role="group" aria-label={label}>
        {options.map((option) => (
          <button
            type="button"
            key={option}
            aria-pressed={value === option}
            onClick={() => onPick(option)}
          >
            {option}
          </button>
        ))}
      </span>
      <select
        className="input seg-pick"
        aria-label={label}
        value={value}
        onChange={(event) => onPick(event.target.value as Option)}
      >
        {options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
    </span>
  );
}

/** Nothing to show: a title, one line on why, and the action that fills it. */
export function Empty({
  title,
  children,
  act,
  onAct,
  compact = false,
}: {
  title: string;
  children?: ReactNode;
  act?: string;
  onAct?: () => void;
  compact?: boolean;
}): JSX.Element {
  return (
    <div className={compact ? "empty empty-c" : "empty"}>
      <strong>{title}</strong>
      {children}
      {act !== undefined ? (
        <button type="button" className="btn btn-quiet btn-sm" onClick={onAct}>
          {act}
        </button>
      ) : null}
    </div>
  );
}

/**
 * The (i) that is open, if any, as the way to shut it.
 *
 * ONE OPEN AT A TIME, HOWEVER IT WAS OPENED. A press outside shuts an open (i)
 * by its mousedown, but Enter and Space open a button with a click and no
 * mousedown, so a keyboard could open a second beside the first and the two
 * popovers would sit over each other's choices. Opening one shuts the other
 * here, which holds for a pointer and a keyboard alike. Found by Bugbot on
 * nova#160.
 */
let shutTheOpenOne: (() => void) | null = null;

/** Help behind an (i). Click or tap opens it; Escape and a click outside close it. */
export function Info({ children, label }: { children: ReactNode; label?: string }): JSX.Element {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);
  const shut = useRef(() => setOpen(false));

  useEffect(() => {
    if (!open) return;
    if (shutTheOpenOne !== shut.current) shutTheOpenOne?.();
    shutTheOpenOne = shut.current;
    const off = (event: MouseEvent): void => {
      if (ref.current && !ref.current.contains(event.target as Node)) setOpen(false);
    };
    const esc = (event: KeyboardEvent): void => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", off);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("mousedown", off);
      document.removeEventListener("keydown", esc);
      if (shutTheOpenOne === shut.current) shutTheOpenOne = null;
    };
  }, [open]);

  return (
    <span className="info" ref={ref}>
      <button
        type="button"
        className="info-b"
        aria-label={label ?? "More about this"}
        aria-expanded={open}
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          setOpen(!open);
        }}
      >
        i
      </button>
      {open ? (
        <span className="info-pop" role="note">
          {children}
        </span>
      ) : null}
    </span>
  );
}

interface MenuOption {
  value?: string | number;
  label?: string;
  title?: string;
  children?: ReactNode;
}

type MenuItem =
  | { kind: "group"; label: string }
  | { kind: "item"; value: string; label: ReactNode; title?: string; sub: boolean };

function menuElement(node: ReactNode): ReactElement<MenuOption> | null {
  return isValidElement<MenuOption>(node) ? node : null;
}

/**
 * A menu that looks like the desk, fed the same option and optgroup children
 * a select would take. It portals into .frame so a card cannot clip it.
 * A value that says "delete" is drawn as a bad action.
 */
export function PopSelect({
  className,
  onChange,
  children,
  title,
  label,
}: {
  className?: string;
  onChange: (event: { target: { value: string } }) => void;
  children: ReactNode;
  title?: string;
  label?: string;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number | null; right: number | null; max: number } | null>(null);
  const btn = useRef<HTMLButtonElement>(null);
  const pop = useRef<HTMLDivElement>(null);
  const items: MenuItem[] = [];
  let head = "";

  const walk = (kids: ReactNode, group: string): void => {
    for (const kid of Array.isArray(kids) ? kids : [kids]) {
      // A list made with .map() arrives as an array inside the children. Left
      // unopened, every option in it was dropped: the estimate's Add menu lost
      // its rate-card lines that way.
      if (Array.isArray(kid)) {
        walk(kid, group);
        continue;
      }
      const element = menuElement(kid);
      if (!element) continue;
      if (element.type === "optgroup") {
        const groupLabel = element.props.label ?? "";
        items.push({ kind: "group", label: groupLabel });
        walk(element.props.children, groupLabel);
        continue;
      }
      if (element.type === "option") {
        const value = element.props.value == null ? "" : String(element.props.value);
        if (value === "" && head === "") {
          head = typeof element.props.children === "string" ? element.props.children : "";
          continue;
        }
        const item: MenuItem = {
          kind: "item",
          value,
          label: element.props.children,
          sub: group !== "",
        };
        if (typeof element.props.title === "string") item.title = element.props.title;
        items.push(item);
      }
    }
  };
  walk(children, "");

  useEffect(() => {
    if (!open) return;
    const button = btn.current;
    if (!button) return;
    const off = (event: MouseEvent): void => {
      const target = event.target as Node;
      if (!button.contains(target) && !(pop.current && pop.current.contains(target))) setOpen(false);
    };
    const esc = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        setOpen(false);
        button.focus();
      }
    };
    const shut = (): void => setOpen(false);
    document.addEventListener("mousedown", off);
    document.addEventListener("keydown", esc);
    window.addEventListener("resize", shut);
    const scroller = button.closest(".main");
    scroller?.addEventListener("scroll", shut, { passive: true });
    return () => {
      document.removeEventListener("mousedown", off);
      document.removeEventListener("keydown", esc);
      window.removeEventListener("resize", shut);
      scroller?.removeEventListener("scroll", shut);
    };
  }, [open]);

  function toggle(): void {
    const button = btn.current;
    const frame = button?.closest(".frame");
    if (!button || !(frame instanceof HTMLElement)) return;
    const buttonBox = button.getBoundingClientRect();
    const frameBox = frame.getBoundingClientRect();
    const scale = frameBox.width / (frame.offsetWidth || frameBox.width) || 1;
    const alignRight = /est-menu/.test(className ?? "");
    setPos({
      top: (buttonBox.bottom - frameBox.top) / scale + 4,
      left: alignRight ? null : (buttonBox.left - frameBox.left) / scale,
      right: alignRight ? (frameBox.right - buttonBox.right) / scale : null,
      max: (frameBox.bottom - buttonBox.bottom) / scale - 16,
    });
    setOpen(!open);
  }

  function pick(value: string): void {
    setOpen(false);
    onChange({ target: { value } });
  }

  const host = btn.current?.closest(".frame");
  const showCaret = /est-add/.test(className ?? "");

  return (
    <>
      <button
        type="button"
        ref={btn}
        className={className ? "pop-b " + className : "pop-b"}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={toggle}
        {...(title !== undefined ? { title } : {})}
        {...(label !== undefined ? { "aria-label": label } : {})}
      >
        {head || "⋯"}
        {showCaret ? <span className="pop-car" aria-hidden="true" /> : null}
      </button>
      {open && host && pos
        ? createPortal(
            <div
              className="pop-menu"
              role="menu"
              ref={pop}
              style={{
                top: pos.top,
                left: pos.left ?? "auto",
                right: pos.right ?? "auto",
                maxHeight: Math.max(200, pos.max),
              }}
            >
              {items.map((item, index) =>
                item.kind === "group" ? (
                  <div className="pop-g" key={"g" + index}>
                    {item.label}
                  </div>
                ) : (
                  <button
                    type="button"
                    role="menuitem"
                    key={item.value + index}
                    className={
                      "pop-i" + (item.sub ? " pop-sub" : "") + (/delete/i.test(item.value) ? " pop-bad" : "")
                    }
                    onClick={() => pick(item.value)}
                    {...(item.title !== undefined ? { title: item.title } : {})}
                  >
                    {item.label}
                  </button>
                ),
              )}
            </div>,
            host,
          )
        : null}
    </>
  );
}

/** A section label can carry its reason behind an (i). */
export function SecLabel({ children, info }: { children: ReactNode; info?: ReactNode }): JSX.Element {
  const label = typeof children === "string" ? children : "this section";
  return (
    <h2 className="sec">
      {children}
      {info !== undefined ? <Info label={"About " + label}>{info}</Info> : null}
    </h2>
  );
}

/**
 * A field's help sits behind an (i) on the label row, beside the words, as the
 * October hand-off draws it (console-parts.jsx FieldRow).
 *
 * THE (i) CANNOT LIVE INSIDE A <label> THAT WRAPS THE CONTROL. A label with no
 * `for` labels its first labelable descendant, and a button is labelable: put
 * the (i) in the label row, ahead of the input, and the label's control becomes
 * the (i). The input then has no name, a press on the words opens the help
 * instead of focusing the field, and nothing on the page looks any different.
 * It is also not valid HTML, which forbids a label holding a labelable element
 * other than its own control.
 *
 * So a row with help is a plain `.field` box whose words are a <label htmlFor>
 * of their own, and the control is handed the id that label names: the one it
 * already carries, or one made here. The (i) sits beside that label, inside
 * `.label`, outside the <label> element. The field's name is then exactly its
 * words, rather than its words followed by "About" and its words again, which
 * is what a screen reader would have read out of a label with a button in it.
 *
 * A row WITHOUT help keeps the wrapping label it always had. Nothing in it can
 * take the control's place, and every screen's checks find their fields as
 * `label.field`; changing the shape where nothing needs changing would only
 * churn them.
 *
 * Every row with help today wraps one native control (an input, a select or a
 * textarea), which is what takes an id. Children of any other shape are drawn
 * as they come and the label then names nothing, which the console's check
 * of this part catches before a screen ships one.
 */
export function FieldRow({
  label,
  help,
  children,
}: {
  label: string;
  help?: ReactNode;
  children?: ReactNode;
}): JSX.Element {
  const made = useId();
  if (help === undefined) {
    return (
      <label className="field">
        <span className="label">{label}</span>
        {children}
      </label>
    );
  }
  const control = Children.count(children) === 1 && isValidElement<{ id?: string }>(children) ? children : null;
  const id = control?.props.id ?? made;
  return (
    <div className="field">
      <span className="label">
        <label htmlFor={id}>{label}</label>
        <Info label={`About ${label}`}>{help}</Info>
      </span>
      {control !== null ? cloneElement(control, { id }) : children}
    </div>
  );
}

/**
 * The first filter row on a page sticks under the bar, slides away while you
 * scroll down, and comes back the moment you scroll up.
 *
 * The row itself sticks. Climbing to the page wrapper translated the whole
 * form off the stage (Voice, the queue) and it did not come back.
 */
export function useStickyFilters(deps: DependencyList): void {
  useEffect(() => {
    const stage = document.getElementById("stage");
    const main = stage?.closest(".main");
    if (!stage || !(main instanceof HTMLElement)) return;
    let last = main.scrollTop;

    // The first .filters on the page, never one in a drawer, a modal or a
    // dialog. Inside a card it is lifted to the block that holds the card in the
    // page's stack, the prototype's rule (desk-kit.jsx), so the card sticks
    // whole — the console's Find an activation. Elsewhere the row sticks on its
    // own, so a page whose wrapper is not a .stack (Social's Voice) cannot be
    // lifted whole and hidden on the way down.
    const mark = (): HTMLElement | null => {
      let found: HTMLElement | null = stage.querySelector<HTMLElement>(".filters");
      if (found?.closest(".drawer, .modal, dialog")) found = null;
      // A list page's right-hand column (DeskPage's `side`) sticks whole, and
      // only the column: climbing on to the block that holds it would take the
      // list with it. From 1100px up furniture.css pins it and switches the
      // hiding off; under that, where it sits above the list, it is this row.
      const side = found?.closest<HTMLElement>(".desk-cols > .side") ?? null;
      if (side !== null) found = side;
      else if (found?.closest(".panel")) {
        while (found && found.parentElement && found.parentElement !== stage && !found.parentElement.classList.contains("stack")) {
          found = found.parentElement;
        }
      }
      stage.querySelectorAll(".filters-sticky").forEach((node) => {
        if (node !== found) node.classList.remove("filters-sticky", "is-up");
      });
      if (!(found instanceof HTMLElement)) return null;
      let top = 0;
      main.querySelectorAll(".topbar, .tabs").forEach((el) => {
        if (el instanceof HTMLElement && !stage.contains(el) && getComputedStyle(el).position === "sticky") {
          top += el.offsetHeight;
        }
      });
      found.classList.add("filters-sticky");
      found.style.setProperty("--filters-top", top + "px");
      return found;
    };

    let stuck = mark();
    const onScroll = (): void => {
      if (!stuck || !stuck.isConnected) stuck = mark();
      if (!stuck) return;
      const y = main.scrollTop;
      const dy = y - last;
      last = y;
      if (y < 80 || dy < -4) stuck.classList.remove("is-up");
      else if (dy > 4) stuck.classList.add("is-up");
    };
    main.addEventListener("scroll", onScroll, { passive: true });
    const observer = new MutationObserver(() => {
      if (!stuck || !stuck.isConnected) stuck = mark();
    });
    observer.observe(stage, { childList: true, subtree: true });
    return () => {
      main.removeEventListener("scroll", onScroll);
      observer.disconnect();
    };
    // The caller names what should re-bind the row. The effect's body reads
    // the document, not those values.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}

/**
 * Unsaved work. Closing the tab asks first, and `window.__deskDirty` is what
 * the left menu reads before it navigates away.
 */
export function useLeaveGuard(dirty: boolean): void {
  const current = useRef(dirty);
  current.current = dirty;
  useEffect(() => {
    const check = (): boolean => current.current;
    window.__deskDirty = check;
    const before = (event: BeforeUnloadEvent): void => {
      if (!current.current) return;
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", before);
    return () => {
      if (window.__deskDirty === check) window.__deskDirty = null;
      window.removeEventListener("beforeunload", before);
    };
  }, []);
}
